import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback, useMemo } from 'react';

import { useApiClient } from '@@common';

import { DeepOperationModel, NewOperationModel, OperationExecutionTypesModel, OperationExposureModel, OperationsClient, UpdateDescriptionModel } from '../../../api/api';
import { contextDesignerQueryKeys } from '../../ContextDesigner/ContextDesignerQueryKeys';

export const useOperationController = (contextId: string) => {
	const operationsClient = useApiClient(OperationsClient);
	const queryClient = useQueryClient();
	const operationsQueryKey = useMemo(() => contextDesignerQueryKeys.operations(contextId), [contextId]);

	// Fetch operations with React Query - automatic caching and deduplication
	const {
		data: operations = [],
		isLoading: loadingOperations,
		refetch,
	} = useQuery({
		queryKey: operationsQueryKey,
		queryFn: () => operationsClient.getOperationsForContext(contextId),
		enabled: !!contextId,
		staleTime: 30000, // Cache for 30 seconds before considering stale
		gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes after unused
	});

	// Create operation mutation
	const createOperationMutation = useMutation({
		mutationFn: async (params: { name: string; description: string; executionType: OperationExecutionTypesModel; exposure: OperationExposureModel; selectedEntityIds: string[] }) => {
			const newRequest = {
				name: params.name,
				description: params.description,
				executionType: params.executionType,
				exposure: params.exposure,
				referencedSchemas: params.selectedEntityIds.map((id) => ({
					schemaId: id,
					schemaContextId: contextId ?? '',
				})),
			} as NewOperationModel;

			return operationsClient.createOperation(contextId ?? '', newRequest);
		},
		onSuccess: () => {
			// Invalidate and refetch operations after successful create
			queryClient.invalidateQueries({ queryKey: operationsQueryKey });
			console.log('Operation added!');
		},
		onError: (error) => {
			console.error('Failed to add new operation:', error);
		},
	});

	// Delete operation mutation
	const deleteOperationMutation = useMutation({
		mutationFn: async (operationId: string) => {
			await operationsClient.deleteOperation(contextId, operationId);
		},
		onSuccess: () => {
			// Invalidate and refetch operations after successful delete
			queryClient.invalidateQueries({ queryKey: operationsQueryKey });
		},
		onError: (error) => {
			console.error('Failed to delete operation:', error);
			throw error;
		},
	});

	const updateOperationExposureMutation = useMutation({
		mutationFn: async (params: { operationId: string; exposure: OperationExposureModel }) => {
			await operationsClient.setExposure(params.operationId, params.exposure);
			return params;
		},
		onSuccess: ({ operationId, exposure }) => {
			queryClient.setQueryData<DeepOperationModel[]>(
				operationsQueryKey,
				(old) => old?.map((operation) => (operation.id === operationId ? { ...operation, exposure } : operation)) ?? [],
			);
		},
		onError: (error) => {
			console.error('Failed to update operation exposure:', error);
			throw error;
		},
	});

	const updateOperationDescriptionMutation = useMutation({
		mutationFn: async (params: { operationId: string; description: string }) => {
			await operationsClient.updateDescription(params.operationId, { description: params.description } as UpdateDescriptionModel);
			return params;
		},
		onSuccess: ({ operationId, description }) => {
			queryClient.setQueryData<DeepOperationModel[]>(
				operationsQueryKey,
				(old) => old?.map((operation) => (operation.id === operationId ? { ...operation, description } : operation)) ?? [],
			);
		},
		onError: (error) => {
			console.error('Failed to update operation description:', error);
			throw error;
		},
	});

	const updateOperationMutation = useMutation({
		mutationFn: async (params: { operationId: string; name: string; executionType: OperationExecutionTypesModel }) => {
			const current = await operationsClient.getById(contextId, params.operationId);
			let updatedOperation = { name: current.name, executionType: current.executionType };
			if (updatedOperation.name !== params.name) {
				const renamed = await operationsClient.renameOperation(contextId, params.operationId, { name: params.name });
				updatedOperation = renamed.operation;
			}
			if (updatedOperation.executionType !== params.executionType) {
				const updated = await operationsClient.updateOperation(params.operationId, {
					executionType: params.executionType,
				});
				updatedOperation = updated;
			}

			return { operationId: params.operationId, updatedOperation };
		},
		onSuccess: ({ operationId, updatedOperation }) => {
			queryClient.setQueryData<DeepOperationModel[]>(
				operationsQueryKey,
				(old) =>
					old?.map((operation) =>
						operation.id === operationId
							? {
									...operation,
									name: updatedOperation.name,
									executionType: updatedOperation.executionType,
								}
							: operation,
					) ?? [],
			);
		},
		onError: (error) => {
			console.error('Failed to update operation:', error);
		},
		onSettled: () => queryClient.invalidateQueries({
			predicate: (query) => contextDesignerQueryKeys.isForContext(query.queryKey, contextId),
		}),
	});

	// Refresh single operation - optimistically update cache
	const refreshSingleOperation = useCallback(
		async (operationId: string) => {
			try {
				const updated = await operationsClient.getById(contextId, operationId);
				// Optimistically update the cache without full refetch
				queryClient.setQueryData<DeepOperationModel[]>(operationsQueryKey, (old) => old?.map((op) => (op.id === operationId ? updated : op)) ?? []);
			} catch (error) {
				console.error('Failed to refresh operation:', error);
			}
		},
		[operationsClient, contextId, operationsQueryKey, queryClient],
	);

	const getOperationExecutionTypes = useCallback(async () => {
		try {
			return await operationsClient.getOperationExecutionTypes();
		} catch (error) {
			console.error('Failed to load operation execution types:', error);
			return [];
		}
	}, [operationsClient]);

	return {
		operations,
		loadingOperations,
		refreshOperations: async () => {
			await refetch();
		},
		refreshSingleOperation,
		createOperation: async (name: string, description: string, executionType: OperationExecutionTypesModel, exposure: OperationExposureModel, selectedEntityIds: string[]) => {
			const operation = await createOperationMutation.mutateAsync({ name, description, executionType, exposure, selectedEntityIds });
			return operation.id;
		},
		updateOperationExposure: async (operationId: string, exposure: OperationExposureModel) => {
			await updateOperationExposureMutation.mutateAsync({ operationId, exposure });
		},
		updateOperationDescription: async (operationId: string, description: string) => {
			await updateOperationDescriptionMutation.mutateAsync({ operationId, description });
		},
		updateOperation: async (operationId: string, name: string, executionType: OperationExecutionTypesModel) => {
			await updateOperationMutation.mutateAsync({ operationId, name, executionType });
		},
		deleteOperation: async (operationId: string) => {
			await deleteOperationMutation.mutateAsync(operationId);
		},
		getOperationExecutionTypes,
	};
};
